import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";


export type SectionId =
  | "basic"
  | "access"
  | "sales";

type SectionContextProps = {
  activeSection: SectionId;
  setActiveSection: (section: SectionId) => void;
  scrollToSection: (
    sectionId: SectionId,
    behavior?: ScrollBehavior,
    position?: "start" | "end",
  ) => void;
};

const SectionContext = createContext<SectionContextProps | undefined>(
  undefined,
);

type SectionProviderProps = {
  children: ReactNode;
  defaultSection?: SectionId;
};

export function SectionProvider({
  children,
  defaultSection = "basic",
}: SectionProviderProps) {
  const [activeSection, setActiveSection] = useState<SectionId>(defaultSection);

  // Scroll to section
  const scrollToSection = useCallback(
    (
      sectionId: SectionId,
      behavior: ScrollBehavior = "smooth",
      position: "start" | "end" = "start",
    ) => {
      setActiveSection(sectionId);
      const sectionElement = document.getElementById(sectionId);
      if (sectionElement) {
        sectionElement.scrollIntoView({
          behavior,
          block: position,
        });
      }
    },
    [],
  );

  useEffect(() => {
    // Check to make sure code is running in the browser
    if (typeof window !== "undefined") {
      // Runs on mount to go to hashed section
      const hash = window.location.hash.slice(1);
      if (hash) {
        scrollToSection(hash as SectionId, "instant");
      }
    }
  }, [scrollToSection]);

  useEffect(() => {
    // Check to make sure code is running in the browser
    if (typeof window !== "undefined") {
      // Update URL hash when active section changes
      const url = new URL(window.location.href);
      url.hash = activeSection;
      window.history.replaceState({}, "", url.toString());
    }
  }, [activeSection]);

  const contextValue = {
    activeSection,
    setActiveSection,
    scrollToSection,
  };

  return (
    <SectionContext.Provider value={contextValue}>
      {children}
    </SectionContext.Provider>
  );
}

export function useSectionContext() {
  const context = useContext(SectionContext);
  if (context === undefined) {
    throw new Error("useSectionContext must be used within a SectionProvider");
  }
  return context;
}
